<script setup lang="ts">
  import { ref, computed } from 'vue'
  import { CalculatorIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
  import DepreciationCalculator from './calculators/DepreciationCalculator.vue'
  import VehicleDamageCalculator from './calculators/VehicleDamageCalculator.vue'
  import RentalCarCalculator from './calculators/RentalCarCalculator.vue'
  import RepairDamageCalculator from '@/components/app/cases/law_specific_data/calculators/RepairDamageCalculator.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import DialogDescription from '@/components/ui/dialog/DialogDescription.vue'
  import TotalDamageCalculator from '@/components/app/cases/law_specific_data/calculators/TotalDamageCalculator.vue'
  import SeveranceCalculator from '@/components/app/cases/law_specific_data/calculators/SeveranceCalculator.vue'
  import { Command, CommandEmpty, CommandInput } from '@/components/ui/command'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'

  const { trackEvent } = useMetrics()

  const { t } = useTranslation({
    en: {
      calculator: 'Calculator',
      search: 'Search ...',
      noOptionsFound: 'No calculators found',
      trafficLaw: 'Traffic Law',
      laborLaw: 'Labor Law',
      vehicleDamage: 'Vehicle Damage Settlement',
      rentalCar: 'Rental Car Compensation',
      depreciation: 'Depreciation',
      repairDamage: 'Residual damage (repair damage)',
      totalDamage: 'Residual damage (total damage)',
      severance: 'Severance Payment'
    },
    de: {
      calculator: 'Rechner',
      search: 'Suchen ...',
      noOptionsFound: 'Keine Rechner gefunden',
      trafficLaw: 'Verkehrsrecht',
      laborLaw: 'Arbeitsrecht',
      vehicleDamage: 'Abrechnung des Fahrzeugschadens',
      rentalCar: 'Mietwagen Nutzungsausfallentschädigung',
      depreciation: 'Wertminderung',
      repairDamage: 'Restschaden (Reparaturschaden)',
      totalDamage: 'Restschaden (Totalschaden)',
      severance: 'Abfindungsrechner'
    }
  })

  const isDialogOpen = ref(false)
  const isPopoverOpen = ref(false)
  const selectedItem = ref('')
  const forceRefresh = ref(0)

  const trafficLawCalculatorMap = {
    [t('vehicleDamage')]: VehicleDamageCalculator,
    [t('rentalCar')]: RentalCarCalculator,
    [t('depreciation')]: DepreciationCalculator,
    [t('repairDamage')]: RepairDamageCalculator,
    [t('totalDamage')]: TotalDamageCalculator
  }

  const laborLawCalculatorMap = {
    [t('severance')]: SeveranceCalculator
  }

  const calculatorMap = {
    ...trafficLawCalculatorMap,
    ...laborLawCalculatorMap
  }

  const trafficLawCalculatorItems = Object.keys(trafficLawCalculatorMap)
  const laborLawCalculatorItems = Object.keys(laborLawCalculatorMap)
  const calculatorItems = Object.keys(calculatorMap)

  // Map calculator display names to consistent identifiers for metrics
  const getCalculatorType = (displayName: string): string => {
    const calculatorTypeMap: Record<string, string> = {
      [t('vehicleDamage')]: 'vehicle_damage',
      [t('rentalCar')]: 'rental_car',
      [t('depreciation')]: 'depreciation',
      [t('repairDamage')]: 'repair_damage',
      [t('totalDamage')]: 'total_damage',
      [t('severance')]: 'severance'
    }
    return calculatorTypeMap[displayName] || 'unknown'
  }

  const filterQuery = ref('')

  const filteredTrafficLawCalculatorItems = computed(() => {
    return trafficLawCalculatorItems.filter(
      (item) => item.toLowerCase().includes(filterQuery.value.toLowerCase()) ?? false
    )
  })
  const filteredLaborLawCalculatorItems = computed(() => {
    return laborLawCalculatorItems.filter(
      (item) => item.toLowerCase().includes(filterQuery.value.toLowerCase()) ?? false
    )
  })

  function openDialog(item: string) {
    // Force component refresh by incrementing the counter
    forceRefresh.value++
    selectedItem.value = item
    isDialogOpen.value = true
    isPopoverOpen.value = false

    // Track calculator opened event
    const calculatorType = getCalculatorType(item)
    trackEvent(MetricEventType.CALCULATOR_OPENED, {
      calculator_type: calculatorType,
      calculator_display_name: item,
      source: 'law_specific_data_dropdown'
    }).catch((error) => {
      console.error('Failed to track calculator opened event:', error)
    })
  }
</script>

<template>
  <Popover v-model:open="isPopoverOpen">
    <PopoverTrigger>
      <button
        data-cy="calculatorDropdown"
        class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
          font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
          disabled:pointer-events-none disabled:opacity-50"
      >
        <CalculatorIcon class="h-5 w-5" />
        {{ t('calculator') }}
        <ChevronDownIcon class="ml-1 h-4 w-4" />
      </button>
    </PopoverTrigger>
    <PopoverContent
      class="w-80 p-0"
      side="bottom"
      align="end"
    >
      <nav class="space-y-1 p-1">
        <Command
          :search-terms="filterQuery"
          @update:search-term="(value) => (filterQuery = value)"
        >
          <CommandInput
            v-model="filterQuery"
            data-cy="calculatorSearch"
            class="my-2 h-8 rounded-lg border-gray-200 px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500"
            :placeholder="t('search')"
          />
          <CommandEmpty
            v-if="
              filteredTrafficLawCalculatorItems.length === 0 &&
              filteredLaborLawCalculatorItems.length === 0
            "
          >
            {{ t('noOptionsFound') }}
          </CommandEmpty>
          <template v-if="filteredTrafficLawCalculatorItems.length !== 0">
            <div
              class="mb-1 mt-1 inline-block px-2 py-0.5 text-sm leading-5 text-gray-500 font-inter"
            >
              {{ t('trafficLaw') }}
            </div>
            <button
              v-for="item in filteredTrafficLawCalculatorItems"
              :key="item"
              :data-cy="
                item === t('vehicleDamage')
                  ? 'calculatorOption-vehicleDamage'
                  : item === t('rentalCar')
                    ? 'calculatorOption-rentalCar'
                    : item === t('depreciation')
                      ? 'calculatorOption-depreciation'
                      : item === t('repairDamage')
                        ? 'calculatorOption-repairDamage'
                        : item === t('totalDamage')
                          ? 'calculatorOption-totalDamage'
                          : 'calculatorOption-unknown'
              "
              class="flex w-full items-center rounded-sm px-3 py-2.5 font-medium hover:bg-gray-100 focus:bg-gray-100
                focus:outline-none"
              @click="openDialog(item)"
            >
              <span class="truncate">{{ item }}</span>
            </button>
          </template>
          <template v-if="filteredLaborLawCalculatorItems.length !== 0">
            <div
              class="mb-1 mt-2 inline-block px-2 py-0.5 text-sm leading-5 text-gray-500 font-inter"
            >
              {{ t('laborLaw') }}
            </div>
            <button
              v-for="item in filteredLaborLawCalculatorItems"
              :key="item"
              :data-cy="
                item === t('severance') ? 'calculatorOption-severance' : 'calculatorOption-unknown'
              "
              class="flex w-full items-center rounded-sm px-3 py-2.5 font-medium hover:bg-gray-100 focus:bg-gray-100
                focus:outline-none"
              @click="openDialog(item)"
            >
              <span class="truncate">{{ item }}</span>
            </button>
          </template>
        </Command>
      </nav>
    </PopoverContent>
  </Popover>

  <Dialog
    :open="isDialogOpen"
    class="sm:top-[5%]"
    @update:open="isDialogOpen = $event"
  >
    <DialogContent class="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-[600px]">
      <DialogDescription></DialogDescription>
      <DialogHeader>
        <DialogTitle>{{ selectedItem }}</DialogTitle>
      </DialogHeader>
      <div class="flex flex-1 flex-col overflow-hidden">
        <component
          :is="calculatorMap[selectedItem]"
          v-if="selectedItem"
          :key="`${selectedItem}-${forceRefresh}`"
          @close="isDialogOpen = false"
        />
      </div>
    </DialogContent>
  </Dialog>
</template>
